import React from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  makeStyles
} from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { useTranslation } from '../hooks/useTranslation';

const useStyles = makeStyles((theme) => ({
  paginationContainer: {
    backgroundColor: 'var(--ikaro-text-white)',
    borderTop: '1px solid #e0e0e0',
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  pageSizeControl: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  pageSizeSelect: {
    minWidth: 80,
  },
  pageButton: {
    minWidth: 32,
    height: 32,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    border: '1px solid #d0d0d0',
    color: '#666666',
    '&:hover': {
      backgroundColor: 'var(--ikaro-gray-light)',
    },
    '&.active': {
      backgroundColor: 'var(--ikaro-orange-primary)',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'var(--ikaro-orange-hover)',
      },
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  pageButtons: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  infoText: {
    color: '#666666',
    fontSize: '0.875rem',
  },
}));

const JobPagination = ({ 
  page, 
  pageSize, 
  total, 
  totalPages, 
  onPageChange, 
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50, 100],
  maxVisiblePages = 5,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('jobs');

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, total);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const handlePageSizeChange = (event) => {
    onPageSizeChange(parseInt(event.target.value));
  };

  const renderPageButtons = () => {
    const buttons = [];
    
    // Calcular o intervalo de páginas para mostrar
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Ajustar o startPage se necessário
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Adicionar primeira página se não estiver visível
    if (startPage > 1) {
      buttons.push(
        <Button
          key={1}
          className={classes.pageButton}
          onClick={() => handlePageChange(1)}
        >
          1
        </Button>
      );
      
      if (startPage > 2) {
        buttons.push(
          <Typography key="ellipsis1" className={classes.infoText}>
            ...
          </Typography>
        );
      }
    }

    // Adicionar páginas visíveis
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          className={`${classes.pageButton} ${i === page ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    // Adicionar última página se não estiver visível
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <Typography key="ellipsis2" className={classes.infoText}>
            ...
          </Typography>
        );
      }
      
      buttons.push(
        <Button
          key={totalPages}
          className={classes.pageButton}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  if (total === 0) {
    return null;
  }

  return (
    <Box className={classes.paginationContainer}>
      <Box className={classes.leftSection}>
        {/* Controle de itens por página */}
        <Box className={classes.pageSizeControl}>
          <Typography className={classes.infoText}>
            {t('pagination.itemsPerPage')}
          </Typography>
          <FormControl size="small" className={classes.pageSizeSelect}>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              variant="outlined"
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        {/* Informações de exibição */}
        <Typography className={classes.infoText}>
          {t('pagination.showing', { start: startIndex, end: endIndex, total })}
        </Typography>
      </Box>

      <Box className={classes.rightSection}>
        {/* Botão anterior */}
        <Button
          className={classes.pageButton}
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          startIcon={<ChevronLeft />}
        >
          {t('pagination.previous')}
        </Button>

        {/* Botões de páginas */}
        <Box className={classes.pageButtons}>
          {renderPageButtons()}
        </Box>

        {/* Botão próximo */}
        <Button
          className={classes.pageButton}
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          endIcon={<ChevronRight />}
        >
          {t('pagination.next')}
        </Button>
      </Box>
    </Box>
  );
};

export default JobPagination; 